import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import aiVoiceSvg from "/public/aivoice.svg";
import voiceButtonSvg from "/public/voicebutton.svg";
import { useAuth } from "../../providers/AuthContext";

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimerRef = useRef(null);
  const recordingTimeRef = useRef(0);
  const { user } = useAuth();

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    recordingTimeRef.current = recordingTime;
  }, [recordingTime]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleSendMessage = async () => {
    if (inputText.trim() === "") return;
    if (!user || !user.id) {
      console.error("User ID not available");
      return;
    }

    const userMessage = {
      text: inputText,
      sender: "user",
      timestamp: new Date().toISOString(),
    };

    setMessages([...messages, userMessage]);
    setInputText("");
    setIsLoading(true);

    try {
      // Send the user's message to the updated endpoint
      const response = await axios.post(`${API_URL}/chat/${user.id}/query`, {
        question: userMessage.text,
      });

      const botMessage = {
        text:
          response.data.answer ||
          response.data.response ||
          "I processed your request.",
        sender: "bot",
        timestamp: new Date().toISOString(),
      };

      setMessages((prevMessages) => [...prevMessages, botMessage]);
    } catch (error) {
      console.error("Error querying chatbot:", error);
      const errorMessage = {
        text: "Sorry, I encountered an error processing your request. Please try again.",
        sender: "bot",
        timestamp: new Date().toISOString(),
      };
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  // const startRecording = async () => {
  //   try {
  //     const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  //     const mediaRecorder = new MediaRecorder(stream, {
  //       mimeType: "audio/webm",
  //     });
  //     mediaRecorderRef.current = mediaRecorder;
  //     audioChunksRef.current = [];

  //     mediaRecorder.ondataavailable = (event) => {
  //       if (event.data.size > 0) {
  //         audioChunksRef.current.push(event.data);
  //       }
  //     };

  //     mediaRecorder.onstop = async () => {
  //       const audioBlob = new Blob(audioChunksRef.current, {
  //         type: "audio/webm",
  //       });

  //       if (!user || !user.id) {
  //         console.error("User ID not available");
  //         return;
  //       }

  //       setIsLoading(true);

  //       try {
  //         // First, send the audio for transcription
  //         const formData = new FormData();
  //         formData.append("audio", audioBlob, "recording.webm");

  //         let transcription = "";
  //         try {
  //           const transcriptionResponse = await axios.post(
  //             `${API_URL}/speech-to-text`,
  //             formData,
  //             {
  //               headers: {
  //                 "Content-Type": "multipart/form-data",
  //               },
  //             }
  //           );
  //           transcription = transcriptionResponse.data.transcript || "";
  //         } catch (error) {
  //           console.error("Error during speech-to-text:", error);
  //         }

  //         if (transcription) {
  //           const userMessage = {
  //             text: transcription,
  //             sender: "user",
  //             timestamp: new Date().toISOString(),
  //           };
  //           setMessages((prevMessages) => [...prevMessages, userMessage]);
  //           const response = await axios.post(
  //             `${API_URL}/chat/${user.id}/voice`,
  //             {
  //               transcript: transcription,
  //             }
  //           );

  //           // Add the bot's response
  //           if (response.data.answer || response.data.response) {
  //             const botMessage = {
  //               text: response.data.answer || response.data.response,
  //               sender: "bot",
  //               timestamp: new Date().toISOString(),
  //             };
  //             setMessages((prevMessages) => [...prevMessages, botMessage]);
  //           }
  //         } else {
  //           // If we couldn't get a transcription, inform the user
  //           const errorMessage = {
  //             text: "Sorry, I couldn't understand what you said. Please try again or type your message.",
  //             sender: "bot",
  //             timestamp: new Date().toISOString(),
  //           };
  //           setMessages((prevMessages) => [...prevMessages, errorMessage]);
  //         }
  //       } catch (error) {
  //         console.error("Error processing voice input:", error);
  //         const errorMessage = {
  //           text: "Sorry, I encountered an error processing your voice input. Please try again.",
  //           sender: "bot",
  //           timestamp: new Date().toISOString(),
  //         };
  //         setMessages((prevMessages) => [...prevMessages, errorMessage]);
  //       } finally {
  //         setIsLoading(false);
  //       }

  //       // Stop all audio tracks
  //       stream.getTracks().forEach((track) => track.stop());
  //     };

  //     // Set dataavailable event to trigger every second for better user experience
  //     mediaRecorder.start(1000);
  //     setIsRecording(true);

  //     // Add visual feedback for recording duration
  //     setRecordingTime(0);
  //     recordingTimerRef.current = setInterval(() => {
  //       setRecordingTime((prev) => prev + 1);

  //       // Auto-stop after 30 seconds to prevent very long recordings
  //       if (recordingTimeRef.current >= 30) {
  //         stopRecording();
  //       }
  //     }, 1000);
  //   } catch (error) {
  //     console.error("Error starting recording:", error);
  //     alert("Unable to access microphone. Please check your permissions.");
  //   }
  // };

  const startRecording = async () => {
    try {
      // Check if the Web Speech API is available
      if (
        !("webkitSpeechRecognition" in window) &&
        !("SpeechRecognition" in window)
      ) {
        alert(
          "Speech recognition is not supported in your browser. Try Chrome or Edge."
        );
        return;
      }

      // Initialize SpeechRecognition
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      // Configure the recognition
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = "en-US"; // Set language - you can make this dynamic if needed
      recognition.maxAlternatives = 1;

      // Set up the recognition result handler
      recognition.onresult = async (event) => {
        const transcript = event.results[0][0].transcript;
        const confidence = event.results[0][0].confidence;

        console.log(
          `Recognized text: ${transcript} (confidence: ${confidence})`
        );

        if (!user || !user.id) {
          console.error("User ID not available");
          return;
        }

        // Create user message with transcribed text
        const userMessage = {
          text: transcript,
          sender: "user",
          timestamp: new Date().toISOString(),
        };
        setMessages((prevMessages) => [...prevMessages, userMessage]);

        setIsLoading(true);
        try {
          // Send the transcript to your API
          const response = await axios.post(
            `${API_URL}/chat/${user.id}/voice`,
            {
              transcript: transcript,
            }
          );

          // Add the bot's response
          if (response.data.answer || response.data.response) {
            const botMessage = {
              text: response.data.answer || response.data.response,
              sender: "bot",
              timestamp: new Date().toISOString(),
            };
            setMessages((prevMessages) => [...prevMessages, botMessage]);
          }
        } catch (error) {
          console.error("Error processing voice input:", error);
          const errorMessage = {
            text: "Sorry, I encountered an error processing your voice input. Please try again.",
            sender: "bot",
            timestamp: new Date().toISOString(),
          };
          setMessages((prevMessages) => [...prevMessages, errorMessage]);
        } finally {
          setIsLoading(false);
        }
      };

      // Handle errors
      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        if (event.error === "no-speech") {
          const errorMessage = {
            text: "I didn't hear anything. Please try again.",
            sender: "bot",
            timestamp: new Date().toISOString(),
          };
          setMessages((prevMessages) => [...prevMessages, errorMessage]);
        } else {
          const errorMessage = {
            text: "Sorry, there was an error with speech recognition. Please try typing instead.",
            sender: "bot",
            timestamp: new Date().toISOString(),
          };
          setMessages((prevMessages) => [...prevMessages, errorMessage]);
        }
        setIsRecording(false);
        clearInterval(recordingTimerRef.current);
      };

      // When the recognition service ends
      recognition.onend = () => {
        setIsRecording(false);
        clearInterval(recordingTimerRef.current);
      };

      // Also use the microphone for visual feedback and backup
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);

        if (recordingTimeRef.current >= 30) {
          if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.recognition
          ) {
            mediaRecorderRef.current.recognition.stop();
          }
          if (stream) {
            stream.getTracks().forEach((track) => track.stop());
          }
          setIsRecording(false);
          clearInterval(recordingTimerRef.current);
        }
      }, 1000);

      recognition.start();

      mediaRecorderRef.current = { recognition, stream };
    } catch (error) {
      console.error("Error starting speech recognition:", error);
      alert("Unable to access microphone. Please check your permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (mediaRecorderRef.current.recognition) {
        try {
          mediaRecorderRef.current.recognition.stop();
        } catch (e) {
          console.error("Error stopping recognition:", e);
        }
      }
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream
          .getTracks()
          .forEach((track) => track.stop());
      }

      setIsRecording(false);

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  // const stopRecording = () => {
  //   if (mediaRecorderRef.current && isRecording) {
  //     mediaRecorderRef.current.stop();
  //     setIsRecording(false);

  //     // Clear recording timer
  //     if (recordingTimerRef.current) {
  //       clearInterval(recordingTimerRef.current);
  //       recordingTimerRef.current = null;
  //     }
  //   }
  // };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Chatbot Button */}
      <button
        onClick={toggleChat}
        className="bg-[#1F9BFF] p-2 hover:bg-blue-500 w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg transition-all duration-300"
      >
        {isOpen ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
          </svg>
        ) : (
          <img src="/bot.png" alt="Chat Bot" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-[400px] h-[500px] bg-[#EEEEEE] rounded-lg shadow-xl flex flex-col overflow-hidden border border-gray-200">
          {/* Chat Header */}
          <div className="bg-[#1F9BFF] text-white px-4 py-3 flex justify-between items-center shrink-0">
            <div className="flex items-center">
              <img
                src="/ailogo2.svg"
                alt="AI Assistant Logo"
                className="w-6 h-6 mr-2"
              />
              <h3 className="font-medium">AI Assistant</h3>
            </div>
            <button
              onClick={toggleChat}
              className="text-white hover:text-gray-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </div>

          {/* Messages Container */}
          <div className="flex-1 p-4 overflow-y-auto bg-[#EEEEEE]">
            <div className="max-w-full space-y-4">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-500">
                  <img
                    src="/ailogo.svg"
                    alt="AI Assistant Logo"
                    className="w-16 h-16 mb-4"
                  />
                  <p className="text-center">
                    Hello! I'm your AI Assistant.
                    <br />
                    How can I help you today?
                  </p>
                </div>
              ) : (
                <>
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`mb-6 ${
                        message.sender === "user"
                          ? "flex justify-end"
                          : "flex justify-start"
                      }`}
                    >
                      <div
                        className={`flex items-start ${
                          message.sender === "user"
                            ? "flex-row-reverse"
                            : "flex-row"
                        } max-w-[75%]`}
                      >
                        {message.sender === "bot" && (
                          <img
                            src="/ailogo.svg"
                            alt="AI Assistant"
                            className="w-6 h-6 mt-1 mx-2 flex-shrink-0"
                          />
                        )}
                        <div className="flex flex-col">
                          <div
                            className={`px-4 py-3 rounded-lg shadow-sm ${
                              message.sender === "user"
                                ? "bg-[#FFFFFF] text-black rounded-tr-none"
                                : "bg-[#FFFFFF] text-gray-800 rounded-tl-none"
                            }`}
                          >
                            {message.text}
                          </div>
                          <div
                            className={`text-xs text-gray-500 mt-1 ${
                              message.sender === "user"
                                ? "text-right"
                                : "text-left"
                            }`}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
              {isLoading && (
                <div className="mb-3 flex justify-start">
                  <div className="flex items-start max-w-[80%]">
                    <img
                      src="/ailogo.svg"
                      alt="AI Assistant"
                      className="w-6 h-6 mt-1 mx-2 flex-shrink-0"
                    />
                    <div className="px-3 py-2 rounded-lg bg-gray-200 text-gray-800 rounded-tl-none">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                        <div
                          className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                          style={{ animationDelay: "0.2s" }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                          style={{ animationDelay: "0.4s" }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {isRecording && (
              <div className="absolute top-[53px] bottom-[56px] left-0 right-0 flex items-center justify-center bg-[#EEEEEE] bg-opacity-90">
                <div className="relative flex flex-col items-center">
                  <img
                    src="/ailogo.svg"
                    alt="AI Logo"
                    className="w-16 h-16 animate-pulse"
                  />
                  <img
                    src="/ailisten.svg"
                    alt="AI Listening"
                    className="absolute -top-4 -right-4 w-8 h-8"
                  />
                  <div className="mt-4 text-red-500 font-medium">
                    Recording: {formatTime(recordingTime)}
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 p-3 bg-white shrink-0 shadow-inner">
            <div className="relative flex items-center">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleInputKeyPress}
                placeholder="Ask me..."
                className="w-full rounded-lg px-4 py-2 pr-16 focus:outline-none bg-gray-100 shadow-sm"
                disabled={isLoading || isRecording}
              />
              <div className="absolute right-2 flex items-center space-x-1">
                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  className={`p-2 rounded-full transition-colors ${
                    isRecording
                      ? "text-red-500"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                  disabled={isLoading}
                  title={
                    isRecording ? "Stop recording" : "Start voice recording"
                  }
                >
                  <img
                    src={isRecording ? aiVoiceSvg : voiceButtonSvg}
                    alt=""
                    className="w-5 h-5"
                  />
                </button>
                <button
                  onClick={handleSendMessage}
                  className={`p-2 rounded-full transition-colors ${
                    inputText.trim() === "" || isLoading || isRecording
                      ? "text-gray-400"
                      : "text-blue-600 hover:bg-blue-100"
                  }`}
                  disabled={inputText.trim() === "" || isLoading || isRecording}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M15.964.686a.5.5 0 0 0-.65-.65L.767 5.855H.766l-.452.18a.5.5 0 0 0-.082.887l.41.26.001.002 4.995 3.178 3.178 4.995.002.002.26.41a.5.5 0 0 0 .886-.083l6-15Zm-1.833 1.89L6.637 10.07l-.215-.338a.5.5 0 0 0-.154-.154l-.338-.215 7.494-7.494 1.178-.471-.47 1.178Z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBot;
